"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight, History, RefreshCw, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSupabase } from "@/hooks/useSupabase";
import { toast } from "sonner";
import type { AuditoriaEvento } from "@/lib/auditoriaCorrecao";
import {
  ORC_AUDIT_TABLE,
  ORC_FIELD_LABELS,
  type OrcAuditoriaRow,
  type OrcPatchableField,
  eventoBadgeVariant,
  formatOrcValue,
  labelCanalSolicitacao,
} from "@/lib/orcamentoHistorico";

const PAGE_SIZE = 25;

function formatWhen(iso: string) {
  try {
    return format(new Date(iso), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return iso;
  }
}

interface Props {
  selectedYear: string;
  registroIdFilter?: string | null;
  onClearRegistroFilter?: () => void;
  refreshToken?: number;
}

export function OrcamentoHistoricoAlteracoes({
  selectedYear,
  registroIdFilter,
  onClearRegistroFilter,
  refreshToken = 0,
}: Props) {
  const { supabase } = useSupabase();
  const [rows, setRows] = useState<OrcAuditoriaRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [eventoFilter, setEventoFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<OrcAuditoriaRow | null>(null);

  const fetchHistorico = useCallback(async () => {
    if (!selectedYear && !registroIdFilter) return;
    setLoading(true);
    try {
      let q = supabase.from(ORC_AUDIT_TABLE).select("*").order("ocorrido_em", { ascending: false });

      if (registroIdFilter) {
        q = q.eq("registro_id", registroIdFilter);
      } else if (selectedYear) {
        q = q
          .gte("ocorrido_em", `${selectedYear}-01-01T00:00:00`)
          .lte("ocorrido_em", `${selectedYear}-12-31T23:59:59`);
      }

      const { data, error } = await q.limit(500);
      if (error) throw error;
      setRows((data || []) as OrcAuditoriaRow[]);
    } catch (e: unknown) {
      console.error(e);
      const msg = e instanceof Error ? e.message : "Erro ao carregar histórico";
      if (msg.includes("does not exist") || msg.includes("schema cache")) {
        toast.error("Tabela de auditoria do orçado não encontrada. Aplique a migração no Supabase.");
      } else {
        toast.error(msg);
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, selectedYear, registroIdFilter]);

  useEffect(() => {
    fetchHistorico();
    setPage(1);
  }, [fetchHistorico, refreshToken]);

  const filtered = useMemo(() => {
    const n = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (eventoFilter !== "all" && r.evento !== eventoFilter) return false;
      if (!n) return true;
      const hay = [
        r.linha_resumo,
        r.motivo,
        r.origem,
        r.mes_ref,
        r.registro_id,
        r.evento,
        r.solicitado_por,
        r.canal,
        r.referencia_solicitacao,
        r.observacoes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(n);
    });
  }, [rows, search, eventoFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <Card className="min-w-0">
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="h-5 w-5 shrink-0 text-muted-foreground" />
                Histórico de alterações (orçado)
              </CardTitle>
              <CardDescription>
                Importações, correções pela tela e exclusões em{" "}
                <code className="text-xs">solis_orcamento</code>.
                {registroIdFilter ? (
                  <>
                    {" "}
                    Filtrado para um registro.{" "}
                    <button
                      type="button"
                      className="text-primary underline text-xs"
                      onClick={onClearRegistroFilter}
                    >
                      Ver todo o ano
                    </button>
                  </>
                ) : null}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchHistorico} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative w-full sm:max-w-sm sm:flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar motivo, resumo, mês…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={eventoFilter}
              onValueChange={(v) => {
                setEventoFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Evento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os eventos</SelectItem>
                <SelectItem value="INSERT">Inclusão</SelectItem>
                <SelectItem value="UPDATE">Alteração</SelectItem>
                <SelectItem value="DELETE">Exclusão</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground sm:ml-auto">
              {filtered.length} evento(s)
              {registroIdFilter ? " neste registro" : ` em ${selectedYear || "—"}`}
            </p>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Carregando histórico…</p>
          ) : paginated.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Nenhum evento ainda. Correções pela tela gravam UPDATE com origem{" "}
              <code className="text-xs">ui_corrigir</code>.
            </p>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Registrado em</TableHead>
                      <TableHead>Evento</TableHead>
                      <TableHead>Solicitado por</TableHead>
                      <TableHead>Canal</TableHead>
                      <TableHead>Resumo / motivo</TableHead>
                      <TableHead className="w-[100px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="text-xs whitespace-nowrap tabular-nums">
                          {formatWhen(row.ocorrido_em)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={eventoBadgeVariant(row.evento as AuditoriaEvento)}>{row.evento}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[140px] truncate text-xs" title={row.solicitado_por || undefined}>
                          {row.solicitado_por || "—"}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{labelCanalSolicitacao(row.canal)}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs" title={row.motivo || row.observacoes || undefined}>
                          {row.motivo || (row.observacoes ? `${row.observacoes.slice(0, 60)}…` : "—")}
                        </TableCell>
                        <TableCell>
                          <Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => setDetail(row)}>
                            Detalhes
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2">
                <p className="text-sm text-muted-foreground">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm tabular-nums text-muted-foreground">
                    {page} / {totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Detalhe da alteração</SheetTitle>
            <SheetDescription>
              {detail ? formatWhen(detail.ocorrido_em) : ""} · {detail?.evento}
            </SheetDescription>
          </SheetHeader>
          {detail ? <HistoricoDetalhe row={detail} /> : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

function HistoricoDetalhe({ row }: { row: OrcAuditoriaRow }) {
  const diffEntries = row.campos_alterados ? Object.entries(row.campos_alterados) : [];

  return (
    <div className="mt-6 space-y-4 text-sm">
      <dl className="grid gap-2 text-xs">
        <div>
          <dt className="text-muted-foreground">Registro</dt>
          <dd className="font-mono break-all">{row.registro_id || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Mês ref.</dt>
          <dd>{row.mes_ref || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Registrado em (sistema)</dt>
          <dd>{formatWhen(row.ocorrido_em)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Executado por (usuário logado)</dt>
          <dd className="font-mono text-[11px] break-all">{row.alterado_por || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Solicitado por</dt>
          <dd>{row.solicitado_por || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Canal</dt>
          <dd>{labelCanalSolicitacao(row.canal)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Data da solicitação</dt>
          <dd>{row.solicitado_em ? formatWhen(row.solicitado_em) : "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Referência</dt>
          <dd className="break-words">{row.referencia_solicitacao || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Resumo</dt>
          <dd>{row.motivo || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Observações</dt>
          <dd className="whitespace-pre-wrap break-words">{row.observacoes || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Origem técnica</dt>
          <dd>{row.origem || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Linha (resumo)</dt>
          <dd>{row.linha_resumo || "—"}</dd>
        </div>
      </dl>

      {diffEntries.length > 0 ? (
        <div className="space-y-2">
          <p className="font-medium">Campos alterados</p>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campo</TableHead>
                  <TableHead>Antes</TableHead>
                  <TableHead>Depois</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {diffEntries.map(([col, ch]) => (
                  <TableRow key={col}>
                    <TableCell className="text-xs font-medium">
                      {ORC_FIELD_LABELS[col as OrcPatchableField] ?? col}
                    </TableCell>
                    <TableCell className="text-xs max-w-[120px] truncate" title={formatOrcValue(ch.antes)}>
                      {formatOrcValue(ch.antes)}
                    </TableCell>
                    <TableCell className="text-xs max-w-[120px] truncate" title={formatOrcValue(ch.depois)}>
                      {formatOrcValue(ch.depois)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}

      {row.evento === "INSERT" && row.registro_depois ? (
        <JsonBlock title="Registro incluído" data={row.registro_depois} />
      ) : null}
      {row.evento === "DELETE" && row.registro_antes ? (
        <JsonBlock title="Registro excluído" data={row.registro_antes} />
      ) : null}
    </div>
  );
}

function JsonBlock({ title, data }: { title: string; data: Record<string, unknown> }) {
  const keys = [
    "mes",
    "data",
    "valor",
    "centro_resultado",
    "area",
    "sub_area",
    "tipo_receita_despesa",
    "classificacao_receita_despesa",
    "descricao_receita_despesa",
    "cliente_fornecedor",
  ] as const;
  return (
    <div className="space-y-2">
      <p className="font-medium text-sm">{title}</p>
      <ul className="text-xs space-y-1 rounded-md border p-3 bg-muted/30">
        {keys.map((k) =>
          data[k] != null && String(data[k]).trim() !== "" ? (
            <li key={k}>
              <span className="text-muted-foreground">{ORC_FIELD_LABELS[k as OrcPatchableField] ?? k}: </span>
              {formatOrcValue(data[k])}
            </li>
          ) : null
        )}
      </ul>
    </div>
  );
}
